import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { img, inView, rise, stagger } from "./utils";
import Tilt from "./Tilt";

const Placeholder = () => (
  <svg viewBox="0 0 120 60" width="55%" role="img" aria-label="Spectacles" fill="none" stroke="#0b1620" strokeWidth="2.5">
    <circle cx="34" cy="32" r="20" />
    <circle cx="86" cy="32" r="20" />
    <path d="M54 30c4-4 8-4 12 0M14 28L4 20M106 28l10-8" strokeLinecap="round" />
  </svg>
);

const Cta = ({ link, text }) =>
  /^https?:/.test(link) ? (
    <a className="au-btn" href={link} target="_blank" rel="noreferrer">
      {text}
    </a>
  ) : (
    <Link className="au-btn" to={link}>
      {text}
    </Link>
  );

// one information row: text on one side, the images on the other (thumbnails swap the big picture)
const AboutBlock = ({ s, i }) => {
  const list = s.images?.length ? s.images.map((m) => m.image) : s.image ? [s.image] : [];
  const [sel, setSel] = useState(0);
  const flip = i % 2 === 1;

  return (
    <motion.article
      className={`au-block${flip ? " au-block--flip" : ""}`}
      initial={{ opacity: 0, y: 70 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="au-block__text">
        <span className="au-eyebrow">
          <i>{String(i + 1).padStart(2, "0")}</i>
        </span>
        <h3>{s.title}</h3>
        <p>{s.description}</p>
        {s.cta_text && s.cta_link && <Cta link={s.cta_link} text={s.cta_text} />}
      </div>

      <div className="au-block__media-wrap">
        <Tilt className="au-block__media" max={5}>
          {list.length ? (
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={list[sel]}
                src={img(list[sel])}
                alt={s.title}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
              />
            </AnimatePresence>
          ) : (
            <Placeholder />
          )}
        </Tilt>
        {list.length > 1 && (
          <div className="au-block__thumbs" role="group" aria-label={`${s.title} images`}>
            {list.map((src, k) => (
              <button
                key={`${src}-${k}`}
                type="button"
                aria-label={`Show image ${k + 1}`}
                aria-pressed={k === sel}
                onClick={() => setSel(k)}
              >
                <img src={img(src)} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
};

export const AboutBlocks = ({ sections = [] }) => (
  <div className="au-blocks">
    {sections.map((s, i) => (
      <AboutBlock key={s.id || s.section_key} s={s} i={i} />
    ))}
  </div>
);

const PROMISES = [
  ["Distinctive style", "Our curated collection brings together global brands, emerging labels and trend-driven frames, so you always find a style that feels like you."],
  ["Precision you can see", "From advanced eye examinations to accurate measurements and precision fitting, every detail is handled with care to deliver clarity, comfort and confidence."],
  ["Personalised to perfection", "Your vision, lifestyle and preferences are unique. Our experts take the time to understand them and help you find the right combination of frame, fit and lenses."],
  ["Expertise you can trust", "Built on years of experience in eye care and eyewear, our team combines professional knowledge with genuine care to guide you at every step."],
  ["Quality without compromise", "From genuine products to honest advice, we stand by our promise of quality, authenticity and complete peace of mind."],
  ["Reliable and transparent", "We believe trust begins with authenticity. That's why we bring you genuine products, trusted brands and honest recommendations, always."],
];

export const PromiseValues = () => (
  <div className="au-promise">
    <motion.h3
      className="au-h3"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={inView}
      transition={{ duration: 0.7 }}
    >
      More than eyewear. It’s our commitment.
    </motion.h3>
    <motion.div className="au-promise__grid" variants={stagger} initial="hide" whileInView="show" viewport={inView}>
      {PROMISES.map(([t, d], i) => (
        <Tilt key={t} className="au-promise__card" variants={rise} max={6}>
          <span className="au-promise__no">{String(i + 1).padStart(2, "0")}</span>
          <h4>{t}</h4>
          <p>{d}</p>
        </Tilt>
      ))}
    </motion.div>
  </div>
);